// src/hooks/useFormSnapshot.ts
//
// "Has this form changed since it opened?" — the answer a drawer hands to `AppDrawer`'s `isDirty`, so Esc, a click
// outside, Cancel and a swipe down ask "Unsaved changes — discard?" instead of throwing the work away (ROADMAP #123).
//
// The form is compared with a SNAPSHOT taken where the drawer seeds it, not flagged on the first keystroke: undoing an
// edit makes the drawer clean again. Empty text, null and undefined count as the same "nothing", so typing a letter and
// deleting it is not a change, and neither is a field the server sends as null and the input hands back as "".
//
// How to use: seed the form as usual, and call `seedFrom(<the same values>)` in the SAME place; call `clear()` when the
// drawer closes (or let the next `seedFrom` replace it). Until a snapshot exists the form is clean — a drawer still
// loading its record has nothing to lose.
//
// ⚠ A drawer whose record can ARRIVE AGAIN while it is open (the page refreshes it, a late time zone re-reads it) seeds
// with `reseed` instead (ROADMAP #125): the same record keeps every field the user has changed and takes the fresh
// values for the rest; a different record replaces the form. Re-seeding with `seedFrom` there threw the typing away.
import { useCallback, useRef, useState } from "react";

/**
 * The comparable form of a value. "" / null / undefined are one "nothing" — a key holding one compares equal to a key
 * that is absent. A Set or Map compares by its contents (as JSON both are `{}`, so any two would look the same); a Set
 * is ORDER-free. Plain arrays keep their order — sort a pick list before snapshotting it if order means nothing.
 */
export const formSnapshot = (value: unknown): string =>
  JSON.stringify(value, (_key, v) => {
    if (v === "" || v === undefined || v === null) {
      return undefined;
    }

    if (v instanceof Set) {
      return Array.from(v, (x) => formSnapshot(x)).sort();
    }

    if (v instanceof Map) {
      return Array.from(v.entries(), ([k, x]) => [formSnapshot(k), formSnapshot(x)]).sort();
    }

    return v;
  }) ?? "null";

/** Whether `current` differs from a snapshot — and a form with no snapshot yet (still loading) is clean. */
export const isDirtyAgainst = (seed: string | null, current: unknown): boolean => seed !== null && formSnapshot(current) !== seed;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && Object.getPrototypeOf(v) === Object.prototype;

/**
 * Fresh values for the SAME record, laid under the user's edits: a top-level field still holding what it was seeded
 * with (`before`) takes the fresh value (`next`); a field the user changed keeps the change. Fields compare the way the
 * snapshot does, through `comparable` when the form has one. A form that is not a plain object is one field.
 */
export const mergeUntouched = <T,>(current: T, before: T, next: T, comparable: (v: T) => unknown = (v) => v): T => {
  const cur = comparable(current);
  const was = comparable(before);

  if (!isPlainObject(current) || !isPlainObject(next) || !isPlainObject(cur) || !isPlainObject(was)) {
    return formSnapshot(cur) === formSnapshot(was) ? next : current;
  }

  const merged: Record<string, unknown> = { ...next };
  for (const key of Object.keys(current)) {
    if (formSnapshot(cur[key]) !== formSnapshot(was[key])) {
      merged[key] = current[key];
    }
  }

  return merged as T;
};

export interface FormSnapshot<T> {
  /** True once the current values differ from the snapshot. */
  isDirty: boolean;
  /** Take the snapshot — call it with the values the form was just seeded with. */
  seedFrom: (seeded: T) => void;
  /**
   * Seed from a record that may arrive again while the drawer is open. Returns the values to put in the form: `next`
   * for a different record (`recordKey` changed) or the first seed, else `next` under the user's edits
   * (`mergeUntouched`, against the values this hook was last rendered with). The snapshot becomes `next` either way,
   * so an edit the server now agrees with is clean.
   * ⚠ Call it in an effect or a handler, never inside a state updater — it records the snapshot.
   */
  reseed: (next: T, recordKey: string) => T;
  /** Drop the snapshot (the drawer closed): the form is clean until the next seed. */
  clear: () => void;
}

/**
 * @param comparable what is compared, when the form's own shape is not (a pick list whose order means nothing → a Set).
 *   Pass a module-level function: it is read on every render.
 */
export const useFormSnapshot = <T,>(current: T, comparable?: (v: T) => unknown): FormSnapshot<T> => {
  const [seed, setSeed] = useState<string | null>(null);
  // The seeded VALUES and whose they are — what `reseed` lays the user's edits over. A ref: a reseed in the same effect
  // as a seed must see it at once.
  const seededRef = useRef<{ value: T; key: string | null } | null>(null);
  const comparableRef = useRef(comparable);
  comparableRef.current = comparable;
  // What is on screen — the form `reseed` keeps the user's edits from. Set on every render AND by every seed: two seeds
  // with no render between (StrictMode re-runs a mount effect) must merge against the first one's result, not against the
  // empty state it replaced — that blanked an edit form. ⚠ Not synced in a layout effect: StrictMode re-runs THOSE too,
  // between the two seeds, and put the empty state back (seen live, review 2026-10-07).
  const currentRef = useRef(current);
  currentRef.current = current;

  const take = useCallback((value: T, key: string | null) => {
    seededRef.current = { value, key };
    currentRef.current = value;
    setSeed(formSnapshot(comparableRef.current ? comparableRef.current(value) : value));
  }, []);

  const seedFrom = useCallback((seeded: T) => {
    take(seeded, null);
  }, [take]);

  const reseed = useCallback((next: T, recordKey: string): T => {
    const previous = seededRef.current;
    const shown = currentRef.current;
    take(next, recordKey);

    if (!previous || previous.key !== recordKey) {
      return next;
    }

    const merged = mergeUntouched(shown, previous.value, next, comparableRef.current);
    currentRef.current = merged;

    return merged;
  }, [take]);

  const clear = useCallback(() => {
    seededRef.current = null;
    setSeed(null);
  }, []);

  const isDirty = isDirtyAgainst(seed, comparable ? comparable(current) : current);

  return { isDirty, seedFrom, reseed, clear };
};
